import { FC } from 'react';
import { useAuthForgotPasswordRequest, useAuthValidateResetTokenRequest } from './queries/authForgotPassword.generated';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Form } from 'antd';
import { InputOTP } from 'antd-input-otp';
import { useGettext } from '@cranium/i18n';
import type { IdentityTypeEnum } from '@/gql/types.generated';
import { Title } from '@pages/Login/component';
import styled from 'styled-components';
import { Step } from './types';

type CurrentEmailProps = {
  setStep: (data: Step) => void;
  email: string;
};

export const CheckOTP: FC<CurrentEmailProps> = ({ setStep, email }) => {
  const { gettext } = useGettext();
  const { identity } = useParams<{ identity: IdentityTypeEnum }>();
  const recoverPassword = useAuthForgotPasswordRequest();
  const validateToken = useAuthValidateResetTokenRequest();
  const [form] = Form.useForm<{ otp: string[] }>();

  const resendCode = () => {
    return recoverPassword({
      email: email,
      type: identity
    }).then(resp => {
      if (resp?.data?.authForgotPassword?.success) {
        toast(gettext('The OTP code was sent to your email: "%(email)"', { email }), {
          position: 'top-right',
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: false,
          bodyClassName: 'pr-1',
          type: 'success'
        });
      }
    });
  };
  const handleSubmit = () => {
    form.validateFields().then(values => {
      return validateToken({
        email: email,
        type: identity,
        resetToken: values.otp.join('')
      }).then(resp => {
        if (resp?.data?.authValidateResetToken?.success) {
          setStep({ step: 2, email: email, otp: values.otp.join('') });
        }
      });
    });
  };

  return (
    <Main>
      <Title>{gettext('Email Verification')}</Title>
      <Description>
        {gettext('We have sent a code to your Email')}
        <b>{email}</b>
      </Description>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item label={gettext('OTP')} name="otp" rules={[{ required: true, message: gettext('Required') }]}>
          <InputOTP autoSubmit={form} inputType="numeric" />
        </Form.Item>
        <Footer>
          <Info>
            {gettext("Didn't receive a code?")}{' '}
            <button type="button" onClick={resendCode}>
              {gettext('Resend')}
            </button>
          </Info>
          <Button type="primary" onClick={() => handleSubmit()}>
            {gettext('Next')}
          </Button>
        </Footer>
      </Form>
    </Main>
  );
};

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  button {
    border: 0;
    background: transparent;
    color: var(--green-7);
    cursor: pointer;
  }
`;

const Main = styled.div`
  width: 420px;
  max-width: 100%;

  > h1 {
    margin-bottom: 0;
  }
  > form {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
  .input-otp {
    display: flex;
    justify-content: space-between;
  }
  button {
    align-self: flex-end;
  }
  a {
    display: flex;
    align-items: center;
    gap: 4px;
    &:hover {
      color: var(--green-7);
      text-decoration: none;
    }
  }
`;

const Description = styled.p`
  font: var(--font-regular-14);
  color: var(--gray-7);
  display: flex;
  align-items: center;
  gap: 4px;
  b {
    color: var(--gray-10);
  }
`;
